import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Card, Button, Spinner, StatusBadge, PageHeader } from '../components/common';
import SignaturePad from '../components/common/SignaturePad';
import { formatDataOra } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';

// ── API calls flux ────────────────────────────────────────────
const fluxAPI = {
  get:                  (id)  => api.get(`/flux/${id}`).then(r => r.data.data),
  repartizeaza:         (id, d) => api.post(`/flux/${id}/repartizeaza`, d),
  spireAprobare:        (id, d) => api.post(`/flux/${id}/spre-aprobare`, d),
  aproba:               (id, d) => api.post(`/flux/${id}/aproba`, d),
  spireContrasemnare:   (id, d) => api.post(`/flux/${id}/spre-contrasemnare`, d),
  contrasemneaza:       (id, d) => api.post(`/flux/${id}/contrasemneaza`, d),
  spireSemnare:         (id, d) => api.post(`/flux/${id}/spre-semnare`, d),
  semneaza:             (id, d) => api.post(`/flux/${id}/semneaza`, d),
  arhiveaza:            (id, d) => api.post(`/flux/${id}/arhiveaza`, d),
  comentariu:           (id, d) => api.post(`/flux/${id}/comentariu`, d),
  genereazaDocument:    (id, d) => api.post(`/flux/${id}/genereaza-document`, d),
  getTemplates:         ()     => api.get('/flux/templates/lista').then(r => r.data.data),
  getDepartamente:      ()     => api.get('/admin/departamente').then(r => r.data.data),
  getUtilizatori:       ()     => api.get('/users/functionari').then(r => r.data.data),
};

// ── Configurare etape vizuale ─────────────────────────────────
const ETAPE = [
  { key: 'inregistrata',       label: 'Înregistrată',       icon: '📥', color: '#6b7280' },
  { key: 'repartizata',        label: 'Repartizată',        icon: '📋', color: '#3b82f6' },
  { key: 'in_lucru',           label: 'În lucru',           icon: '⚙️',  color: '#8b5cf6' },
  { key: 'spre_aprobare',      label: 'Spre aprobare',      icon: '👁',  color: '#f59e0b' },
  { key: 'aprobata',           label: 'Aprobată',           icon: '✓',   color: '#10b981' },
  { key: 'spre_contrasemnare', label: 'Contrasemnare',      icon: '⚖️',  color: '#7c3aed' },
  { key: 'contrasemnata',      label: 'Contrasemnată',      icon: '✍',   color: '#059669' },
  { key: 'spre_semnare',       label: 'Spre semnare',       icon: '✍️',  color: '#f97316' },
  { key: 'semnata',            label: 'Semnată',            icon: '🖊',  color: '#06b6d4' },
  { key: 'arhivata',           label: 'Arhivată',           icon: '📁',  color: '#374151' },
];

const ACTIUNI_LABELS = {
  inregistrare:            'Înregistrare',
  repartizare:             'Repartizare',
  preluare:                'Preluare',
  trimitere_aprobare:      'Trimis spre aprobare',
  aprobare:                'Aprobat',
  respingere:              'Respins',
  returnare:               'Returnat',
  trimitere_contrasemnare: 'Trimis spre contrasemnare',
  contrasemnare:           'Contrasemnat',
  refuz_contrasemnare:     'Contrasemnare refuzată',
  trimitere_semnare:       'Trimis spre semnare',
  semnare:                 'Semnat',
  arhivare:                'Arhivat',
  comentariu:              'Comentariu',
};

// ── Permisiuni UI (trebuie sincronizate cu backend) ───────────
const ROL_POATE = {
  repartizare:             ['functionar', 'sef_departament', 'primar'],
  trimitere_aprobare:      ['functionar', 'sef_departament'],
  aprobare:                ['sef_departament', 'primar'],
  trimitere_contrasemnare: ['sef_departament', 'primar'],
  contrasemnare:           ['secretar_general'],
  trimitere_semnare:       ['sef_departament', 'primar'],
  semnare:                 ['primar'],
  arhivare:                ['sef_departament', 'primar'],
  comentariu:              ['functionar', 'sef_departament', 'primar', 'secretar_general'],
  genereaza:               ['functionar', 'sef_departament'],
};

function poate(rol, actiune) {
  return (ROL_POATE[actiune] || []).includes(rol);
}

export default function FluxPage() {
  const { cerereId } = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const qc           = useQueryClient();
  const [actiuneActiva, setActiuneActiva] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [documentIdPentruSemnare, setDocumentIdPentruSemnare] = useState('');

  const { data, isLoading } = useQuery(
    ['flux', cerereId],
    () => fluxAPI.get(cerereId),
    { enabled: !!cerereId, refetchInterval: 30000 }
  );

  const { data: departamente } = useQuery('departamente', fluxAPI.getDepartamente);
  const { data: utilizatori }  = useQuery('utilizatori',  fluxAPI.getUtilizatori);
  const { data: templates }    = useQuery('templates',    fluxAPI.getTemplates);

  function invalidate() {
    qc.invalidateQueries(['flux', cerereId]);
    qc.invalidateQueries(['cerere', cerereId]);
  }

  function mutatie(apiFn, mesajOk) {
    return useMutation(
      (data) => apiFn(cerereId, data),
      {
        onSuccess: () => { toast.success(mesajOk); invalidate(); setActiuneActiva(null); setShowSignaturePad(false); },
        onError:   (e) => toast.error(e.response?.data?.message || 'Eroare'),
      }
    );
  }

  const mutRepartizeaza       = mutatie(fluxAPI.repartizeaza,       'Cerere repartizată');
  const mutAprobare           = mutatie(fluxAPI.spireAprobare,      'Trimisă spre aprobare');
  const mutAproba             = mutatie(fluxAPI.aproba,             'Decizie înregistrată');
  const mutContrasemnare      = mutatie(fluxAPI.spireContrasemnare, 'Trimisă spre contrasemnare');
  const mutContrasemneaza     = mutatie(fluxAPI.contrasemneaza,     'Decizie contrasemnare înregistrată');
  const mutSemnare            = mutatie(fluxAPI.spireSemnare,       'Trimisă spre semnare');
  const mutSemneaza           = mutatie(fluxAPI.semneaza,           'Document semnat cu succes');
  const mutArhiveaza          = mutatie(fluxAPI.arhiveaza,          'Dosar arhivat');
  const mutComentariu         = mutatie(fluxAPI.comentariu,         'Comentariu adăugat');
  const mutDocument           = mutatie(fluxAPI.genereazaDocument,  'Document generat');

  if (isLoading) return <Layout><Spinner message="Se încarcă fluxul..." /></Layout>;

  const flux        = data?.flux;
  const cerereStatus = data?.cerereStatus;
  const istoric     = data?.istoric || [];
  const documente   = data?.documente || [];
  const pasiTemplate = data?.pasiTemplate || null;

  // Filtrăm etapele în funcție de template-ul activ (dacă există)
  const etapeVizibile = pasiTemplate
    ? ETAPE.filter(e => pasiTemplate.includes(e.key))
    : ETAPE;

  const STATUS_TO_ETAPA = { in_lucru: 'in_lucru', rezolvata: 'arhivata' };
  const etapaFlux    = flux?.etapa_curenta || 'inregistrata';
  const etapaCerere  = STATUS_TO_ETAPA[cerereStatus] || null;
  const etapaCurenta = (etapaFlux === 'repartizata' && etapaCerere) ? etapaCerere : etapaFlux;
  const etapaIdx     = etapeVizibile.findIndex(e => e.key === etapaCurenta);
  const rol          = user?.rol || '';

  function handleSemneaza(svgData, biometricData) {
    if (!documentIdPentruSemnare) {
      toast.error('Selectați documentul de semnat');
      return;
    }
    mutSemneaza.mutate({ documentId: documentIdPentruSemnare, svgData, biometricData });
  }

  return (
    <Layout>
      <div style={s.backRow}>
        <button onClick={() => navigate(`/cereri/${cerereId}`)} style={s.back}>← Înapoi la cerere</button>
      </div>

      <PageHeader title="Flux document" subtitle={`Etapă curentă: ${ETAPE.find(e => e.key === etapaCurenta)?.label || etapaCurenta}`} />

      {/* Bara progres etape */}
      <Card>
        <div style={s.progresBar}>
          {etapeVizibile.map((etapa, idx) => {
            const trecut = idx < etapaIdx;
            const activ  = idx === etapaIdx;
            return (
              <div key={etapa.key} style={s.etapaItem}>
                <div style={{
                  ...s.etapaCircle,
                  backgroundColor: trecut ? '#10b981' : activ ? etapa.color : '#e5e7eb',
                  color: (trecut || activ) ? '#fff' : '#9ca3af',
                  border: activ ? `3px solid ${etapa.color}` : 'none',
                }}>
                  {trecut ? '✓' : etapa.icon}
                </div>
                <div style={{
                  ...s.etapaLabel,
                  color:      activ ? etapa.color : trecut ? '#10b981' : '#9ca3af',
                  fontWeight: activ ? '700' : '400',
                }}>
                  {etapa.label}
                </div>
                {idx < etapeVizibile.length - 1 && (
                  <div style={{ ...s.etapaLinie, backgroundColor: trecut ? '#10b981' : '#e5e7eb' }} />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <div style={s.grid2}>
        {/* Coloana stânga — acțiuni disponibile */}
        <div>
          <Card title="Acțiuni disponibile">
            <div style={s.actiuniGrid}>

              {/* Repartizare */}
              {etapaCurenta === 'inregistrata' && poate(rol, 'repartizare') && (
                <Button onClick={() => setActiuneActiva('repartizeaza')}>📋 Repartizează</Button>
              )}
              {['repartizata','in_lucru'].includes(etapaCurenta) && poate(rol, 'repartizare') && (
                <Button onClick={() => setActiuneActiva('repartizeaza')} variant="secondary">🔄 Re-repartizează</Button>
              )}

              {/* Spre aprobare */}
              {['repartizata','in_lucru'].includes(etapaCurenta) && poate(rol, 'trimitere_aprobare') && (
                <Button onClick={() => setActiuneActiva('spre_aprobare')}>👁 Spre aprobare</Button>
              )}

              {/* Aprobare / Respingere */}
              {etapaCurenta === 'spre_aprobare' && poate(rol, 'aprobare') && (
                <>
                  <Button onClick={() => mutAproba.mutate({ aprobat: true })} style={{ backgroundColor: '#10b981' }}>✓ Aprobă</Button>
                  <Button onClick={() => setActiuneActiva('respinge')} variant="danger">✗ Respinge</Button>
                </>
              )}

              {/* Spre contrasemnare (acte administrative) */}
              {etapaCurenta === 'aprobata' && poate(rol, 'trimitere_contrasemnare') && (
                <Button onClick={() => setActiuneActiva('spre_contrasemnare')} style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none' }}>
                  ⚖️ Spre contrasemnare
                </Button>
              )}

              {/* Contrasemnare de secretar general */}
              {etapaCurenta === 'spre_contrasemnare' && poate(rol, 'contrasemnare') && (
                <>
                  <Button onClick={() => mutContrasemneaza.mutate({ aprobat: true })} style={{ backgroundColor: '#059669', color: '#fff', border: 'none' }}>✓ Contrasemnează</Button>
                  <Button onClick={() => setActiuneActiva('refuz_contrasemnare')} variant="danger">✗ Refuză</Button>
                </>
              )}

              {/* Spre semnare */}
              {['aprobata','contrasemnata'].includes(etapaCurenta) && poate(rol, 'trimitere_semnare') && (
                <Button onClick={() => setActiuneActiva('spre_semnare')}>✍️ Spre semnare</Button>
              )}

              {/* Semnare — primar cu pad */}
              {etapaCurenta === 'spre_semnare' && poate(rol, 'semnare') && !showSignaturePad && (
                <Button onClick={() => setShowSignaturePad(true)} style={{ backgroundColor: '#f97316', color: '#fff', border: 'none' }}>
                  🖊 Semnează
                </Button>
              )}

              {/* Arhivare */}
              {['semnata','aprobata','contrasemnata','respinsa'].includes(etapaCurenta) && poate(rol, 'arhivare') && (
                <Button onClick={() => setActiuneActiva('arhiveaza')} variant="secondary">📁 Arhivează</Button>
              )}

              {/* Comentariu și generare document */}
              {poate(rol, 'comentariu') && (
                <Button onClick={() => setActiuneActiva('comentariu')} variant="ghost">💬 Comentariu</Button>
              )}
              {poate(rol, 'genereaza') && (
                <Button onClick={() => setActiuneActiva('genereaza')} variant="ghost">📄 Generează document</Button>
              )}

            </div>
          </Card>

          {/* Pad semnătură primar */}
          {showSignaturePad && etapaCurenta === 'spre_semnare' && poate(rol, 'semnare') && (
            <Card title="Semnătură digitală — Primar">
              <div style={fld}>
                <label style={lbl}>Document de semnat *</label>
                <select style={inp} value={documentIdPentruSemnare}
                  onChange={e => setDocumentIdPentruSemnare(e.target.value)}>
                  <option value="">Selectează documentul...</option>
                  {documente.map(d => (
                    <option key={d.id} value={d.id}>{d.titlu} ({d.status})</option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: '16px' }}>
                <SignaturePad
                  label="Semnați în spațiul de mai jos (primar)"
                  onSave={handleSemneaza}
                  onCancel={() => setShowSignaturePad(false)}
                />
              </div>
              {mutSemneaza.isLoading && <p style={{ color: '#888', textAlign: 'center', marginTop: '8px' }}>Se procesează semnătura...</p>}
            </Card>
          )}

          {/* Formular acțiune activă */}
          {actiuneActiva && (
            <Card title={getTitluActiune(actiuneActiva)}>
              <FormActiune
                actiune={actiuneActiva}
                departamente={departamente || []}
                utilizatori={utilizatori || []}
                templates={templates || []}
                documente={documente}
                onSubmit={(data) => {
                  if (actiuneActiva === 'repartizeaza')         mutRepartizeaza.mutate(data);
                  if (actiuneActiva === 'spre_aprobare')        mutAprobare.mutate(data);
                  if (actiuneActiva === 'respinge')             mutAproba.mutate({ aprobat: false, ...data });
                  if (actiuneActiva === 'spre_contrasemnare')   mutContrasemnare.mutate(data);
                  if (actiuneActiva === 'refuz_contrasemnare')  mutContrasemneaza.mutate({ aprobat: false, ...data });
                  if (actiuneActiva === 'spre_semnare')         mutSemnare.mutate(data);
                  if (actiuneActiva === 'arhiveaza')            mutArhiveaza.mutate(data);
                  if (actiuneActiva === 'comentariu')           mutComentariu.mutate(data);
                  if (actiuneActiva === 'genereaza')            mutDocument.mutate(data);
                }}
                onCancel={() => setActiuneActiva(null)}
                loading={mutRepartizeaza.isLoading || mutAprobare.isLoading || mutAproba.isLoading ||
                         mutContrasemnare.isLoading || mutContrasemneaza.isLoading ||
                         mutSemnare.isLoading || mutArhiveaza.isLoading || mutComentariu.isLoading}
              />
            </Card>
          )}

          {/* Documente generate */}
          {documente.length > 0 && (
            <Card title={`Documente (${documente.length})`}>
              {documente.map(doc => (
                <div key={doc.id} style={s.docRow}>
                  <div style={s.docInfo}>
                    <span style={s.docTip}>{doc.tip}</span>
                    <span style={s.docTitlu}>{doc.titlu}</span>
                    <span style={s.docMeta}>v{doc.versiune} · {doc.status} · {formatDataOra(doc.created_at)}</span>
                  </div>
                  <div style={s.docActiuni}>
                    <StatusBadge status={doc.status === 'semnat' ? 'rezolvata' : 'inregistrata'} />
                    {doc.minio_key && (
                      <Button
                        variant="ghost"
                        style={{ padding: '4px 10px', fontSize: '12px' }}
                        onClick={async () => {
                          const r = await api.get(`/documente/${doc.id}/download`);
                          window.open(r.data.url, '_blank');
                        }}
                      >↓ Descarcă</Button>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Coloana dreapta — istoricul fluxului */}
        <div>
          <Card title="Istoricul fluxului">
            {istoric.length === 0
              ? <p style={{ color: '#888', textAlign: 'center', padding: '24px' }}>Nicio acțiune înregistrată</p>
              : istoric.map((item, i) => (
                <div key={item.id || i} style={{ ...s.istoricItem, ...(item.intern ? s.istoricIntern : {}) }}>
                  <div style={s.istoricHeader}>
                    <span style={s.istoricActiune}>{ACTIUNI_LABELS[item.actiune] || item.actiune}</span>
                    {item.intern && <span style={s.internBadge}>intern</span>}
                  </div>
                  {item.etapa_la && (
                    <div style={s.etapaTransitie}>
                      {item.etapa_de_la && <span style={s.etapaVeche}>{item.etapa_de_la}</span>}
                      {item.etapa_de_la && <span style={{ color: '#aaa', margin: '0 6px' }}>→</span>}
                      <span style={s.etapaNouă}>{item.etapa_la}</span>
                    </div>
                  )}
                  {item.comentariu && <p style={s.istoricComentariu}>{item.comentariu}</p>}
                  {item.document_titlu && <p style={s.istoricDoc}>📄 {item.document_titlu}</p>}
                  <div style={s.istoricMeta}>
                    <span>{item.efectuat_de_nume || 'Sistem'}</span>
                    <span>{formatDataOra(item.created_at)}</span>
                  </div>
                </div>
              ))
            }
          </Card>
        </div>
      </div>
    </Layout>
  );
}

// ── Formular dinamic pentru fiecare acțiune ───────────────────
function FormActiune({ actiune, departamente, utilizatori, templates, documente, onSubmit, onCancel, loading }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {actiune === 'repartizeaza' && (
        <>
          <div style={fld}>
            <label style={lbl}>Departament *</label>
            <select style={inp} value={form.departamentId || ''} onChange={e => set('departamentId', e.target.value)}>
              <option value="">Selectează...</option>
              {departamente.map(d => <option key={d.id} value={d.id}>{d.nume}</option>)}
            </select>
          </div>
          <div style={fld}>
            <label style={lbl}>Funcționar responsabil</label>
            <select style={inp} value={form.repartizatLaId || ''} onChange={e => set('repartizatLaId', e.target.value)}>
              <option value="">Selectează (opțional)...</option>
              {(utilizatori || []).filter(u => ['functionar','sef_departament'].includes(u.rol)).map(u => (
                <option key={u.id} value={u.id}>{u.prenume} {u.nume} — {u.departament}</option>
              ))}
            </select>
          </div>
          <div style={fld}>
            <label style={lbl}>Termen soluționare</label>
            <input type="date" style={inp} value={form.termenSolutionare || ''} onChange={e => set('termenSolutionare', e.target.value)} />
          </div>
        </>
      )}

      {actiune === 'respinge' && (
        <div style={fld}>
          <label style={lbl}>Motiv respingere *</label>
          <textarea rows={3} style={{ ...inp, resize: 'vertical' }}
            value={form.motivRespingere || ''}
            onChange={e => set('motivRespingere', e.target.value)}
            placeholder="Explicați motivul respingerii..."
          />
        </div>
      )}

      {actiune === 'refuz_contrasemnare' && (
        <div style={fld}>
          <label style={lbl}>Motiv refuz contrasemnare *</label>
          <textarea rows={3} style={{ ...inp, resize: 'vertical' }}
            value={form.motivRefuz || ''}
            onChange={e => set('motivRefuz', e.target.value)}
            placeholder="Explicați motivul refuzului..."
          />
        </div>
      )}

      {actiune === 'spre_contrasemnare' && documente.length > 0 && (
        <div style={fld}>
          <label style={lbl}>Document spre contrasemnare</label>
          <select style={inp} value={form.documentId || ''} onChange={e => set('documentId', e.target.value)}>
            <option value="">Selectează (opțional)...</option>
            {documente.map(d => <option key={d.id} value={d.id}>{d.titlu} ({d.status})</option>)}
          </select>
        </div>
      )}

      {actiune === 'spre_semnare' && (
        <div style={fld}>
          <label style={lbl}>Document de semnat *</label>
          <select style={inp} value={form.documentId || ''} onChange={e => set('documentId', e.target.value)}>
            <option value="">Selectează documentul...</option>
            {documente.map(d => <option key={d.id} value={d.id}>{d.titlu} ({d.status})</option>)}
          </select>
        </div>
      )}

      {actiune === 'arhiveaza' && (
        <div style={fld}>
          <label style={lbl}>Număr arhivă</label>
          <input type="text" style={inp} placeholder="ex: ARH-2026-001"
            value={form.numarArhiva || ''} onChange={e => set('numarArhiva', e.target.value)} />
        </div>
      )}

      {actiune === 'genereaza' && (
        <>
          <div style={fld}>
            <label style={lbl}>Template document *</label>
            <select style={inp} value={form.templateCod || ''} onChange={e => set('templateCod', e.target.value)}>
              <option value="">Selectează template...</option>
              {(templates || []).map(t => <option key={t.id} value={t.cod}>{t.titlu}</option>)}
            </select>
          </div>
          <div style={fld}>
            <label style={lbl}>Conținut răspuns</label>
            <textarea rows={4} style={{ ...inp, resize: 'vertical' }}
              value={form.variabileExtra?.CONTINUT_RASPUNS || ''}
              onChange={e => set('variabileExtra', { ...form.variabileExtra, CONTINUT_RASPUNS: e.target.value })}
              placeholder="Textul răspunsului către cetățean..."
            />
          </div>
        </>
      )}

      {actiune !== 'arhiveaza' && actiune !== 'respinge' && actiune !== 'refuz_contrasemnare' && (
        <div style={fld}>
          <label style={lbl}>{actiune === 'comentariu' ? 'Comentariu *' : 'Comentariu (opțional)'}</label>
          <textarea rows={2} style={{ ...inp, resize: 'vertical' }}
            value={form.comentariu || ''}
            onChange={e => set('comentariu', e.target.value)}
            placeholder="Note sau observații..."
          />
        </div>
      )}

      {actiune === 'comentariu' && (
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.intern || false} onChange={e => set('intern', e.target.checked)} />
          Comentariu intern (invizibil pentru cetățean)
        </label>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <Button onClick={() => onSubmit(form)} disabled={loading}>
          {loading ? 'Se procesează...' : 'Confirmă'}
        </Button>
        <Button onClick={onCancel} variant="ghost">Anulează</Button>
      </div>
    </div>
  );
}

function getTitluActiune(actiune) {
  const titluri = {
    repartizeaza:        'Repartizare cerere',
    spre_aprobare:       'Trimite spre aprobare',
    respinge:            'Respinge cererea',
    spre_contrasemnare:  'Trimite spre contrasemnare',
    refuz_contrasemnare: 'Refuz contrasemnare',
    spre_semnare:        'Trimite spre semnare',
    arhiveaza:           'Arhivare dosar',
    comentariu:          'Adaugă comentariu',
    genereaza:           'Generează document',
  };
  return titluri[actiune] || actiune;
}

// ── Styles ────────────────────────────────────────────────────
const s = {
  backRow:        { marginBottom: '16px' },
  back:           { background: 'none', border: 'none', cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', padding: 0 },
  progresBar:     { display: 'flex', alignItems: 'flex-start', padding: '8px 0', gap: 0 },
  etapaItem:      { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: 1, minWidth: 0 },
  etapaCircle:    { width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', flexShrink: 0, zIndex: 1 },
  etapaLabel:     { fontSize: '10px', textAlign: 'center', marginTop: '6px', lineHeight: '1.3', maxWidth: '80px' },
  etapaLinie:     { position: 'absolute', top: '18px', left: '50%', right: '-50%', height: '2px', zIndex: 0 },
  grid2:          { display: 'grid', gridTemplateColumns: '1fr 360px', gap: '16px', alignItems: 'start' },
  actiuniGrid:    { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  docRow:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5', gap: '12px' },
  docInfo:        { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 },
  docTip:         { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  docTitlu:       { fontSize: '14px', fontWeight: '500', color: '#1a1a2e' },
  docMeta:        { fontSize: '11px', color: '#aaa' },
  docActiuni:     { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  istoricItem:    { padding: '12px 0', borderBottom: '1px solid #f5f5f5' },
  istoricIntern:  { backgroundColor: '#fffbeb', padding: '10px 12px', borderRadius: '8px', margin: '4px 0' },
  istoricHeader:  { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  istoricActiune: { fontWeight: '600', fontSize: '13px', color: '#1a1a2e' },
  internBadge:    { fontSize: '10px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '10px' },
  etapaTransitie: { display: 'flex', alignItems: 'center', margin: '4px 0', fontSize: '12px' },
  etapaVeche:     { color: '#aaa', fontFamily: 'monospace' },
  etapaNouă:      { color: '#2d2d7e', fontWeight: '600', fontFamily: 'monospace' },
  istoricComentariu: { margin: '4px 0', fontSize: '13px', color: '#444', lineHeight: '1.5', whiteSpace: 'pre-wrap' },
  istoricDoc:     { margin: '4px 0', fontSize: '12px', color: '#666' },
  istoricMeta:    { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#aaa', marginTop: '6px' },
};
const fld = { display: 'flex', flexDirection: 'column', gap: '4px' };
const lbl = { fontSize: '13px', fontWeight: '500', color: '#555' };
const inp = { padding: '8px 12px', fontSize: '14px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', fontFamily: 'inherit' };
